import omit from 'lodash/omit'

import { GOOGLE_AUTOMATION_STAGE_STATUSES } from './googleAutomationStageStatuses'
import * as googleUrls from './googleUrls'
import { GoogleSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { StageType, GoogleRecommendationKey } from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import SectionNotFoundException from '../exception/sectionNotFoundException'
import {
  getWaitForElementToAppearPromise,
  wait,
} from '../interruptiblePromises'
import { evaluateXpath } from '../interruptibleXpath'
import {
  AutomationErrors,
  defaultCantUndoFn,
  getCurrentValueFnFromKey,
  getErrorIsDisabledAndReasonFn,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import { getElement, getOptionalElement, shouldUpdateSetting } from '../utils'

const getCurrentValueFn = (key: GoogleSettingKey) =>
  getCurrentValueFnFromKey(key, (value) => value)

const getIsFulfilledFn = (key: GoogleSettingKey) =>
  getIsFulfilledFnFromKey(key, (value) => value === 'Off')

const getCantUndoFn = () => defaultCantUndoFn

function getIsDisabledFn(settingKey: GoogleSettingKey) {
  return (
    automationErrors: Partial<Record<GoogleSettingKey, AutomationErrors>>,
  ) => getErrorIsDisabledAndReasonFn(settingKey)(automationErrors).isDisabled
}

function getIsDisabledReasonFn(settingKey: GoogleSettingKey) {
  return (
    automationErrors: Partial<Record<GoogleSettingKey, AutomationErrors>>,
  ) =>
    getErrorIsDisabledAndReasonFn(settingKey)(automationErrors).isDisabledReason
}

type Title =
  | 'Web & App Activity'
  | 'Timeline'
  | 'YouTube History'
  | 'Personalized Ads'

async function getCurrentValue(
  title: Title,
  allowUnavailable?: boolean,
): Promise<'Off' | 'On' | null> {
  if (title === 'Personalized Ads') {
    // make sure we are on the correct page
    await getElement<HTMLDivElement>(document, '//span[text()="My Ad Center"]')
    if (allowUnavailable && window.location.pathname === '/kids') {
      // Setting is unavailable for kid/teen Google accounts
      return null
    }
    return window.location.pathname.endsWith('home') ? 'On' : 'Off'
  }
  let groupEl: HTMLDivElement | null | undefined
  const xpath = `//div[text()="${title}"]/..`
  if (!allowUnavailable) {
    groupEl = await getElement<HTMLDivElement>(document, xpath)
  } else {
    groupEl = await getOptionalElement<HTMLDivElement>(document, xpath)
    if (!groupEl) {
      // Setting is unavailable for kid/teen Google accounts
      return null
    }
  }
  const buttonEl = await getElement<HTMLButtonElement>(
    groupEl,
    './/button[descendant::span[starts-with(text(),"Turn")]]',
  )
  const currentValue = buttonEl.textContent.includes('Turn off') ? 'On' : 'Off'
  return currentValue
}

const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'GATHER_GOOGLE_ACTIVITY_CONTROLS_WEB_APP_ACTIVITY',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.activityControlsUrl.navigateForUser(googleAccountIndex)

      const currentValue = await getCurrentValue('Web & App Activity')
      await userContext.updateStoredInitialValues(
        { [GoogleSettingKey.WEB_APP_ACTIVITY]: currentValue },
        GoogleRecommendationKey.GOOGLE_ACTIVITY_CONTROLS,
      )
    },
  },
  {
    type: StageType.ACTION,
    key: 'GATHER_GOOGLE_ACTIVITY_CONTROLS_TIMELINE',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.activityControlsUrl.navigateForUser(googleAccountIndex)

      const currentValue = await getCurrentValue('Timeline', true)
      if (currentValue === null) {
        await userContext.updateStoredAutomationErrors(
          {
            [GoogleSettingKey.TIMELINE]: {
              gatherError: {
                message:
                  'Timeline is not available for kid/teen Google accounts',
                name: SectionNotFoundException.exceptionName,
              },
            },
          },
          GoogleRecommendationKey.GOOGLE_ACTIVITY_CONTROLS,
        )
        // Unset TIMELINE's initial values so update and undo know that the
        // setting is not available
        await userContext.updateStoredInitialValues(
          omit(userContext.initialValues, GoogleSettingKey.TIMELINE),
          GoogleRecommendationKey.GOOGLE_ACTIVITY_CONTROLS,
        )
        return
      }
      await userContext.updateStoredInitialValues(
        { [GoogleSettingKey.TIMELINE]: currentValue },
        GoogleRecommendationKey.GOOGLE_ACTIVITY_CONTROLS,
      )
    },
  },
  {
    type: StageType.ACTION,
    key: 'GATHER_GOOGLE_ACTIVITY_CONTROLS_YOUTUBE_HISTORY',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.activityControlsUrl.navigateForUser(googleAccountIndex)

      const currentValue = await getCurrentValue('YouTube History')
      await userContext.updateStoredInitialValues(
        { [GoogleSettingKey.YOUTUBE_HISTORY]: currentValue },
        GoogleRecommendationKey.GOOGLE_ACTIVITY_CONTROLS,
      )
    },
  },
  {
    type: StageType.ACTION,
    key: 'GATHER_GOOGLE_ACTIVITY_CONTROLS_PERSONALIZED_ADS',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    urlPattern: googleUrls.myAdCenterUrl.urlPattern,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.myAdCenterUrl.navigateForUser(googleAccountIndex)

      const currentValue = await getCurrentValue('Personalized Ads', true)
      if (currentValue === null) {
        await userContext.updateStoredAutomationErrors(
          {
            [GoogleSettingKey.PERSONALIZED_ADS]: {
              gatherError: {
                message:
                  'Personalized ads are not available for kid/teen Google accounts',
                name: SectionNotFoundException.exceptionName,
              },
            },
          },
          GoogleRecommendationKey.GOOGLE_ACTIVITY_CONTROLS,
        )
        // Unset PERSONALIZED_ADS's initial values so update and undo know that
        // the setting is not available
        await userContext.updateStoredInitialValues(
          omit(userContext.initialValues, GoogleSettingKey.PERSONALIZED_ADS),
          GoogleRecommendationKey.GOOGLE_ACTIVITY_CONTROLS,
        )
        return
      }
      await userContext.updateStoredInitialValues(
        { [GoogleSettingKey.PERSONALIZED_ADS]: currentValue },
        GoogleRecommendationKey.GOOGLE_ACTIVITY_CONTROLS,
      )
    },
  },
]

async function turnOff(title: Title, userContext): Promise<void> {
  if (title === 'Personalized Ads') {
    // make sure we are on the correct page
    await getElement<HTMLDivElement>(document, '//span[text()="My Ad Center"]')

    let turnOffButtonEl = await getElement<HTMLButtonElement>(
      document,
      '//button[contains(@aria-label, "Turn off")]',
    )
    turnOffButtonEl.click()
    turnOffButtonEl = await getElement<HTMLButtonElement>(
      document,
      '//span[text()="Turn off"]/..',
    )
    if (!userContext.isDry) {
      turnOffButtonEl.click()
      const gotItButtonEl = await getElement<HTMLButtonElement>(
        document,
        '//span[text()="Got it"]/..',
      )
      gotItButtonEl.click()
    } else {
      const cancelButtonEl = await getElement<HTMLButtonElement>(
        document,
        '//span[text()="Cancel"]/..',
      )
      cancelButtonEl.click()
    }
  } else {
    const groupEl = await getElement<HTMLDivElement>(
      document,
      `//div[text()="${title}"]/..`,
    )
    const buttonEl = await getElement<HTMLButtonElement>(groupEl, './/button')
    // Double check that it's not already off
    const currentValue = buttonEl.textContent.includes('Turn off')
      ? 'On'
      : 'Off'
    const targetValue = 'Off'
    if (currentValue === targetValue) {
      return
    }
    await wait(500)
    buttonEl.click()
    if (title === 'YouTube History') {
      const pauseButtonEl = await getElement<HTMLButtonElement>(
        document,
        '//span[text()="Pause"]',
      )
      await wait(500)
      if (!userContext.isDry) {
        pauseButtonEl.click()
        const cWizEl = await getElement<HTMLElement>(
          document,
          '//div[text()="Not saving new YouTube History"]/ancestor-or-self::c-wiz',
        )
        const closeButtonEl = await getElement<HTMLButtonElement>(
          cWizEl,
          './/button[@aria-label="Close this dialog"]',
        )
        await wait(500)
        closeButtonEl.click()
      } else {
        // Just close the popup menu in dry mode
        buttonEl.click()
      }
    } else {
      const menuEl = await getElement<HTMLUListElement>(
        groupEl,
        './/ul[@aria-label="Turn off setting and delete activity menu"]',
      )
      const turnOffMenuItemEl = evaluateXpath<HTMLLIElement>(
        menuEl,
        './li',
      ).find((el) => el.textContent.includes('Turn off'))
      await wait(500)
      if (!userContext.isDry) {
        turnOffMenuItemEl.click()
        const closeButtonEl = await getElement<HTMLButtonElement>(
          document,
          '//button[@aria-label="Close this dialog"]',
        )
        await wait(500)
        closeButtonEl.click()
        const gotItButtonEl =
          await getWaitForElementToAppearPromise<HTMLButtonElement>(
            document,
            (node) =>
              evaluateXpath<HTMLButtonElement>(
                node,
                '//span[text()="Got it"]/..',
              )[0],
            5000,
          )
        if (gotItButtonEl) {
          gotItButtonEl.click()
        }
      } else {
        // Just close the popup menu in dry mode
        buttonEl.click()
      }
    }
  }
}

const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'UPDATE_GOOGLE_ACTIVITY_CONTROLS_INTERSTITIAL_1',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.CHANGING_PAGES,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.homeUrl.navigateForUser(googleAccountIndex)
    },
  },
  {
    type: StageType.ACTION,
    key: 'UPDATE_GOOGLE_ACTIVITY_CONTROLS_WEB_APP_ACTIVITY',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.activityControlsUrl.navigateForUser(googleAccountIndex)

      if (
        !shouldUpdateSetting(
          userContext.settingsSelected,
          userContext.initialValues,
          GoogleSettingKey.WEB_APP_ACTIVITY,
          getIsFulfilledFn,
        )
      ) {
        return
      }
      const currentValue = await getCurrentValue('Web & App Activity')
      const targetValue = 'Off'
      if (currentValue === targetValue) {
        return
      }
      await turnOff('Web & App Activity', userContext)
    },
  },
  {
    type: StageType.ACTION,
    key: 'UPDATE_GOOGLE_ACTIVITY_CONTROLS_INTERSTITIAL_2',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.CHANGING_PAGES,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.homeUrl.navigateForUser(googleAccountIndex)
    },
  },
  {
    type: StageType.ACTION,
    key: 'UPDATE_GOOGLE_ACTIVITY_CONTROLS_TIMELINE',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.activityControlsUrl.navigateForUser(googleAccountIndex)

      if (
        !shouldUpdateSetting(
          userContext.settingsSelected,
          userContext.initialValues,
          GoogleSettingKey.TIMELINE,
          getIsFulfilledFn,
        )
      ) {
        return
      }
      const currentValue = await getCurrentValue('Timeline')
      const targetValue = 'Off'
      if (currentValue === targetValue) {
        return
      }
      await turnOff('Timeline', userContext)
    },
  },
  {
    type: StageType.ACTION,
    key: 'UPDATE_GOOGLE_ACTIVITY_CONTROLS_INTERSTITIAL_3',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.CHANGING_PAGES,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.homeUrl.navigateForUser(googleAccountIndex)
    },
  },
  {
    type: StageType.ACTION,
    key: 'UPDATE_GOOGLE_ACTIVITY_CONTROLS_YOUTUBE_HISTORY',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.activityControlsUrl.navigateForUser(googleAccountIndex)

      if (
        !shouldUpdateSetting(
          userContext.settingsSelected,
          userContext.initialValues,
          GoogleSettingKey.YOUTUBE_HISTORY,
          getIsFulfilledFn,
        )
      ) {
        return
      }
      const currentValue = await getCurrentValue('YouTube History')
      const targetValue = 'Off'
      if (currentValue === targetValue) {
        return
      }
      await turnOff('YouTube History', userContext)
    },
  },
  {
    type: StageType.ACTION,
    key: 'UPDATE_GOOGLE_ACTIVITY_CONTROLS_PERSONALIZED_ADS',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.myAdCenterUrl.navigateForUser(googleAccountIndex)

      if (
        !shouldUpdateSetting(
          userContext.settingsSelected,
          userContext.initialValues,
          GoogleSettingKey.PERSONALIZED_ADS,
          getIsFulfilledFn,
        )
      ) {
        return
      }
      const currentValue = await getCurrentValue('Personalized Ads')
      const targetValue = 'Off'
      if (currentValue === targetValue) {
        return
      }
      await turnOff('Personalized Ads', userContext)
    },
  },
]

async function turnOn(
  title: Title,
  cancelLabel: string,
  userContext,
): Promise<void> {
  if (title === 'Personalized Ads') {
    // make sure we are on the correct page
    await getElement<HTMLDivElement>(document, '//span[text()="My Ad Center"]')
    const dropdownButton = await getElement<HTMLButtonElement>(
      document,
      '//button[@aria-label="Personalized ads are off. Turn on."]',
    )
    dropdownButton.click()

    // check if the setting is already on
    let turnOnButtonEl = await getElement<HTMLButtonElement>(
      document,
      '//button[normalize-space(.)="Turn on"]',
    )
    turnOnButtonEl.click()
    let dialogEl = await getElement<HTMLDivElement>(
      document,
      '//span[text()="Turn on personalized ads?"]/ancestor-or-self::div[@role="dialog"]',
    )
    turnOnButtonEl = await getElement<HTMLButtonElement>(
      dialogEl,
      './/span[text()="Turn on"]/..',
    )
    if (!userContext.isDry) {
      turnOnButtonEl.click()
      dialogEl = await getElement<HTMLDivElement>(
        document,
        '//span[text()="Personalized ads are now on"]/ancestor-or-self::div[@role="dialog"]',
      )
      const gotItButtonEl = await getElement<HTMLButtonElement>(
        dialogEl,
        '//span[text()="Got it"]/..',
      )
      gotItButtonEl.click()
    } else {
      const cancelButtonEl = await getElement<HTMLButtonElement>(
        dialogEl,
        `//span[text()="${cancelLabel}"]/..`,
      )
      cancelButtonEl.click()
    }
  } else {
    const groupEl = await getElement<HTMLDivElement>(
      document,
      `//div[text()="${title}"]/..`,
    )
    const buttonEl = await getElement<HTMLButtonElement>(groupEl, './/button')
    // Double check that it's not already on
    const currentValue = buttonEl.textContent.includes('Turn off')
      ? 'On'
      : 'Off'
    const targetValue = 'On'
    if (currentValue === targetValue) {
      return
    }
    await wait(500)
    buttonEl.click()
    let cWizXpath = `//*[starts-with(text(), "Turn on ${title}?")]/ancestor-or-self::c-wiz`
    if (title === 'YouTube History') {
      // No trailing question mark in the YouTube History turn-on dialog
      cWizXpath = `//*[starts-with(text(), "Turn on ${title}")]/ancestor-or-self::c-wiz`
    }
    let cWizEl = await getWaitForElementToAppearPromise(
      document,
      (node) => evaluateXpath(node, cWizXpath)[0],
      5000,
    )
    if (!cWizEl && title !== 'YouTube History') {
      // If we don't find the dialog with a trailing question mark, try without
      // one. It's been the case where people (Dhruvil) get an entirely
      // different dialog than the ones we get, probably due to geolocation
      // differences, and their dialog doesn't have the trailing question mark.
      //
      // (We exclude 'YouTube History' in this logic because it never has a
      // trailing question mark.)
      cWizXpath = `//*[starts-with(text(), "Turn on ${title}")]/ancestor-or-self::c-wiz`
      // Fail here, though, if we can't find it
      cWizEl = await getElement<HTMLElement>(document, cWizXpath)
    }
    const turnOnButtonEl = await getElement<HTMLButtonElement>(
      cWizEl,
      './/span[text()="Turn on"]/..',
    )
    if (!userContext.isDry) {
      await wait(500)
      turnOnButtonEl.disabled = false
      await wait(500)
      turnOnButtonEl.click()
      // Refresh cWizEl as it has changed
      cWizEl = await getElement<HTMLDivElement>(
        document,
        './/div[text()="Setting is on"]/ancestor-or-self::c-wiz',
      )
      await getElement<HTMLDivElement>(
        cWizEl,
        `.//div[text()="Saving ${title}"]`,
      )
      const closeButtonEl = await getElement<HTMLButtonElement>(
        cWizEl,
        './/button[@aria-label="Close this dialog"]',
      )
      await wait(500)
      closeButtonEl.click()
    } else {
      // Just close the popup menu in dry mode
      const cancelButtonEl = await getElement<HTMLButtonElement>(
        cWizEl,
        `//span[text()="${cancelLabel}"]/..`,
      )
      await wait(500)
      cancelButtonEl.click()
    }
  }
}

const undoStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'UNDO_GOOGLE_ACTIVITY_CONTROLS_WEB_APP_ACTIVITY',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.activityControlsUrl.navigateForUser(googleAccountIndex)

      if (
        !shouldUpdateSetting(
          userContext.settingsSelected,
          userContext.initialValues,
          GoogleSettingKey.WEB_APP_ACTIVITY,
          getIsFulfilledFn,
        )
      ) {
        return
      }
      const currentValue = await getCurrentValue('Web & App Activity')
      const initialValue =
        userContext.initialValues?.[GoogleSettingKey.WEB_APP_ACTIVITY]
      if (initialValue === 'On' && currentValue === 'Off') {
        await turnOn('Web & App Activity', 'No thanks', userContext)
      }
      if (initialValue === 'Off' && currentValue === 'On') {
        await turnOff('Web & App Activity', userContext)
      }
    },
  },
  {
    type: StageType.ACTION,
    key: 'UNDO_GOOGLE_ACTIVITY_CONTROLS_TIMELINE',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.activityControlsUrl.navigateForUser(googleAccountIndex)

      if (
        !shouldUpdateSetting(
          userContext.settingsSelected,
          userContext.initialValues,
          GoogleSettingKey.TIMELINE,
          getIsFulfilledFn,
        )
      ) {
        return
      }
      const currentValue = await getCurrentValue('Timeline')
      const initialValue =
        userContext.initialValues?.[GoogleSettingKey.TIMELINE]
      if (initialValue === 'On' && currentValue === 'Off') {
        await turnOn('Timeline', 'Cancel', userContext)
      }
      if (initialValue === 'Off' && currentValue === 'On') {
        await turnOff('Timeline', userContext)
      }
    },
  },
  {
    type: StageType.ACTION,
    key: 'UNDO_GOOGLE_ACTIVITY_CONTROLS_YOUTUBE_HISTORY',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.activityControlsUrl.navigateForUser(googleAccountIndex)

      if (
        !shouldUpdateSetting(
          userContext.settingsSelected,
          userContext.initialValues,
          GoogleSettingKey.YOUTUBE_HISTORY,
          getIsFulfilledFn,
        )
      ) {
        return
      }
      const currentValue = await getCurrentValue('YouTube History')
      const initialValue =
        userContext.initialValues?.[GoogleSettingKey.YOUTUBE_HISTORY]
      if (initialValue === 'On' && currentValue === 'Off') {
        await turnOn('YouTube History', 'No thanks', userContext)
      }
      if (initialValue === 'Off' && currentValue === 'On') {
        await turnOff('YouTube History', userContext)
      }
    },
  },
  {
    type: StageType.ACTION,
    key: 'UNDO_GOOGLE_ACTIVITY_CONTROLS_PERSONALIZED_ADS',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.myAdCenterUrl.navigateForUser(googleAccountIndex)

      if (
        !shouldUpdateSetting(
          userContext.settingsSelected,
          userContext.initialValues,
          GoogleSettingKey.PERSONALIZED_ADS,
          getIsFulfilledFn,
        )
      ) {
        return
      }
      const currentValue = await getCurrentValue('Personalized Ads')
      const initialValue =
        userContext.initialValues?.[GoogleSettingKey.PERSONALIZED_ADS]
      if (initialValue === 'On' && currentValue === 'Off') {
        await turnOn('Personalized Ads', 'Cancel', userContext)
      }
      if (initialValue === 'Off' && currentValue === 'On') {
        await turnOff('Personalized Ads', userContext)
      }
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
